import { FormEvent, useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import type { Session } from "@supabase/supabase-js";
import { supabase } from "../lib/supabase";
import { useStaffRole } from "../lib/admin";
import { canManage, managedOrgs, orgApi } from "../lib/org";
import { BOARD, KIND_LABEL, VESSEL_BOARD, isVessel, itemStatus, worst, type Tier, type Vehicle } from "../lib/fleet";
import RecipientPicker from "../ui/RecipientPicker";
import { iconSvg } from "../lib/icons";
import AppFooter from "../ui/AppFooter";
import CompanyBadge from "../ui/CompanyBadge";
import TeamPresence from "../ui/TeamPresence";
import AdminLink from "../ui/AdminLink";
import { Drawer, Field, Notice } from "./admin/ui";
import VehicleDrawer from "./fleet/VehicleDrawer";
import UserMenu from "../ui/UserMenu";

type Org = { id: string; name: string; reminder_emails: string[]; reminder_days: number[]; contact_email: string | null };
type Dev = { id: string; name: string; org_id: string; icon: string | null };

export default function FleetPage({ session }: { session: Session }) {
  const staff = useStaffRole(session.user.id);
  const [orgs, setOrgs] = useState<Org[]>([]);
  const [org, setOrg] = useState<string>("");
  const [vehicles, setVehicles] = useState<Vehicle[] | null>(null);
  const [devices, setDevices] = useState<Dev[]>([]);
  const [managed, setManaged] = useState<Set<string> | "all" | null>(null);
  const [tab, setTab] = useState<"register" | "board">("register");
  const [showCancelled, setShowCancelled] = useState(false);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState<Vehicle | "new" | null>(null);
  const [preset, setPreset] = useState<string | null>(null); // tracker chosen from the "not in fleet yet" list
  const [settings, setSettings] = useState(false);

  useEffect(() => {
    managedOrgs(session.user.id).then(setManaged);
    supabase.from("organisations").select("id, name, reminder_emails, reminder_days, contact_email").neq("status", "closed").order("name")
      .then(({ data }) => { const o = (data ?? []) as Org[]; setOrgs(o); setOrg((cur) => cur || o.find((x) => x.name.includes("Skyward"))?.id || o[0]?.id || ""); });
  }, [session.user.id]);

  const load = useCallback(async () => {
    if (!org) return;
    const [{ data: v }, { data: d }] = await Promise.all([
      supabase.from("vehicles").select("*, compliance_items(*), vehicle_documents(id, vehicle_id, item_id, name, mime, size_bytes, uploaded_at)").eq("org_id", org).order("reg"),
      supabase.from("devices").select("id, name, org_id, icon").eq("org_id", org).neq("status", "retired").order("name"),
    ]);
    setVehicles((v ?? []) as Vehicle[]);
    setDevices((d ?? []) as Dev[]);
  }, [org]);
  useEffect(() => { setVehicles(null); load(); }, [load]);

  const cur = orgs.find((o) => o.id === org);
  const warn = Math.max(...((cur?.reminder_days?.length ? cur.reminder_days : [60]) as number[]), 7);
  const editable = canManage(managed, org);
  const list = useMemo(() => {
    const t = q.trim().toLowerCase();
    return (vehicles ?? []).filter((v) => (showCancelled || v.status === "active") &&
      (!t || [v.reg, v.fleet_no, v.make_model, v.project, v.color].some((x) => x?.toLowerCase().includes(t))));
  }, [vehicles, q, showCancelled]);

  // KPIs over active vehicles
  const active = (vehicles ?? []).filter((v) => v.status === "active");
  const tally = { expired: 0, due: 0, susp: 0 };
  const actions: { v: Vehicle; label: string; text: string; sub: string; tier: Tier; days: number }[] = [];
  for (const v of active) for (const it of v.compliance_items) {
    const s = itemStatus(it, warn);
    if (s.tier === "expired") tally.expired++; else if (s.tier === "due") tally.due++; else if (s.tier === "susp") tally.susp++;
    if (s.tier === "expired" || s.tier === "due") actions.push({ v, label: it.label, text: s.text, sub: s.sub, tier: s.tier, days: s.days });
  }
  actions.sort((a, b) => a.days - b.days);
  const devName = (id: string | null) => devices.find((d) => d.id === id)?.name;
  const linkedIds = new Set((vehicles ?? []).map((v) => v.device_id).filter(Boolean));
  const unlinked = devices.filter((d) => !linkedIds.has(d.id));

  return (
    <div className="admin fleet">
      <header className="topbar">
        <img src="/logo.png" alt="Tracking Fiji" className="topbar-logo" />
        <span className="beta">Fleet</span>
        <div className="topbar-spacer" />
        <CompanyBadge userId={session.user.id} />
        <TeamPresence />
        <Link className="btn-ghost btn-map" to="/map">← Map</Link>
        <Link className="btn-ghost" to="/company">Company</Link>
        <Link className="btn-ghost" to="/reports">Reports</Link>
        <AdminLink role={staff} />
        <UserMenu email={session.user.email} />
      </header>
      <main className="amain">
        <div className="atoolbar">
          <h1>Vehicles, vessels &amp; equipment</h1>
          {orgs.length > 1 && (
            <select className="asearch" value={org} onChange={(e) => setOrg(e.target.value)}>
              {orgs.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
            </select>
          )}
          <div className="rep-actions">
            {editable && <button className="btn-ghost" onClick={() => setSettings(true)}>Reminder settings</button>}
            {editable && <button className="btn-primary" onClick={() => { setPreset(null); setOpen("new"); }}>+ Add to fleet</button>}
          </div>
        </div>

        <p className="fleet-intro">Asset register for administrative management: licence renewals, insurance and document control for vehicles and vessels, with reminders before anything expires.</p>
        {editable && vehicles && unlinked.length > 0 && (
          <div className="fleet-suggest">
            <div>
              <b>{unlinked.length} tracker{unlinked.length === 1 ? " isn't" : "s aren't"} in the fleet register yet</b>
              <span className="sub">Add them to keep their registration, insurance and documents with the live tracking.</span>
            </div>
            <div className="fs-chips">
              {unlinked.slice(0, 8).map((d) => (
                <button key={d.id} className="fs-chip" onClick={() => { setPreset(d.id); setOpen("new"); }} title={`Add ${d.name} to the fleet`}>
                  <span className="licon stopped" dangerouslySetInnerHTML={{ __html: iconSvg(d.icon, 13) }} />{d.name}<span className="fs-add">+ Add</span>
                </button>
              ))}
              {unlinked.length > 8 && <span className="sub">and {unlinked.length - 8} more in “+ Add to fleet”</span>}
            </div>
          </div>
        )}
        <div className="rep-tiles">
          <div><b className="mono">{active.length}</b><span>In service</span></div>
          <div className={tally.expired ? "t-bad" : ""}><b className="mono">{tally.expired}</b><span>Expired items</span></div>
          <div className={tally.due ? "t-warn" : ""}><b className="mono">{tally.due}</b><span>Due within {warn} days</span></div>
          <div><b className="mono">{tally.susp}</b><span>Suspended items</span></div>
        </div>

        {actions.length > 0 && (
          <div className="await actions-box">
            <h3>Action needed — {actions.length} item{actions.length === 1 ? "" : "s"}</h3>
            {actions.slice(0, 8).map((a, i) => (
              <div key={i} className="await-row" onClick={() => setOpen(a.v)} role="button">
                <span className={`tier-dot t-${a.tier}`} /><b>{a.v.reg}</b><span className="sub">{a.v.make_model}</span>
                <span>{a.label}</span><span className={`tier-txt t-${a.tier}`}>{a.text} · {a.sub}</span>
              </div>
            ))}
            {actions.length > 8 && <p className="sub">…and {actions.length - 8} more on the compliance board.</p>}
          </div>
        )}

        <div className="atoolbar">
          <nav className="subtabs" style={{ margin: 0 }}>
            <button className={tab === "register" ? "on" : ""} onClick={() => setTab("register")}>Fleet register</button>
            <button className={tab === "board" ? "on" : ""} onClick={() => setTab("board")}>Compliance board</button>
          </nav>
          <input className="asearch" placeholder="Search reg, model, project…" value={q} onChange={(e) => setQ(e.target.value)} />
          <label className="acheck"><input type="checkbox" checked={showCancelled} onChange={(e) => setShowCancelled(e.target.checked)} /> Show cancelled</label>
        </div>

        {vehicles === null ? <div className="rep-empty">Loading…</div> : !list.length ? (
          <div className="rep-empty">{vehicles.length ? "No vehicles match." : editable ? "No vehicles yet — add your first one." : "No vehicles recorded yet."}</div>
        ) : tab === "register" ? (
          <div className="vgrid">
            {list.map((v) => {
              const w = worst(v, warn);
              return (
                <button key={v.id} className={`vcard ${v.status === "cancelled" ? "cancelled" : ""}`} onClick={() => setOpen(v)}>
                  <div className="vcard-top"><b className="vreg">{v.reg}</b><span className={`apill ${v.status === "active" ? "ok" : "plain"}`}>{v.status === "active" ? "In service" : "Cancelled"}</span></div>
                  <div className="vmodel">{isVessel(v) && <span className="vtag">Vessel</span>}{v.make_model ?? KIND_LABEL[v.kind]}</div>
                  <div className="sub">{[v.fleet_no, v.year, v.color].filter(Boolean).join(" · ")}</div>
                  <div className={`vworst t-${w.tier}`}><span className={`tier-dot t-${w.tier}`} />{w.text}</div>
                  <div className="vmeta sub">
                    {devName(v.device_id) ? <>📍 {devName(v.device_id)}</> : "No tracker linked"}
                    {v.vehicle_documents.length > 0 && <> · {v.vehicle_documents.length} doc{v.vehicle_documents.length === 1 ? "" : "s"}</>}
                    {v.project && <> · {v.project}</>}
                  </div>
                </button>
              );
            })}
          </div>
        ) : (
          <>
            {([["Vehicles & equipment", BOARD, list.filter((v) => !isVessel(v))], ["Marine vessels", VESSEL_BOARD, list.filter(isVessel)]] as const)
              .filter(([, , rows]) => rows.length).map(([heading, cols, rows]) => (
              <div className="atable-wrap board-wrap" key={heading}>
                {list.some(isVessel) && list.some((v) => !isVessel(v)) && <h3 className="board-h">{heading}</h3>}
                <table className="atable board">
                  <thead><tr><th>{heading === "Marine vessels" ? "Vessel" : "Vehicle"}</th>{cols.map(([k, l]) => <th key={k}>{l}</th>)}<th>Other</th></tr></thead>
                  <tbody>
                    {rows.map((v) => {
                      const other = v.compliance_items.filter((i) => !cols.some(([k]) => k === i.kind));
                      return (
                        <tr key={v.id} onClick={() => setOpen(v)}>
                          <td><b>{v.reg}</b>{v.status === "cancelled" && <span className="sub"> (cancelled)</span>}<div className="sub">{v.make_model}</div></td>
                          {cols.map(([k]) => {
                            const it = v.compliance_items.find((i) => i.kind === k);
                            const st = itemStatus(it, warn);
                            return <td key={k}><span className={`cell t-${st.tier}`}>{st.text}<small>{st.sub}</small></span></td>;
                          })}
                          <td>{other.map((it) => { const st = itemStatus(it, warn); return <div key={it.id} className={`cell-mini t-${st.tier}`}>{it.label}: {st.text}</div>; })}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ))}
          </>
        )}
        <p className="fine" style={{ textAlign: "left" }}>Expiry warnings start {warn} days ahead. Cancelled vehicles are not counted.</p>
      </main>

      {open && <VehicleDrawer key={open === "new" ? "new" : open.id} vehicleId={open === "new" ? null : open.id} org={org} devices={devices}
        linked={(vehicles ?? []).map((v) => v.device_id).filter(Boolean) as string[]}
        editable={editable} warn={warn} presetDevice={open === "new" ? preset : null} onClose={() => setOpen(null)} onChanged={load} />}
      <AppFooter />
      {settings && cur && <ReminderSettings org={cur} onClose={() => setSettings(false)}
        onSaved={(o) => { setOrgs((all) => all.map((x) => (x.id === o.id ? { ...x, ...o } : x))); setSettings(false); }} />}
    </div>
  );
}

function ReminderSettings({ org, onClose, onSaved }: { org: Org; onClose: () => void; onSaved: (o: Partial<Org> & { id: string }) => void }) {
  const [to, setTo] = useState<string[]>(org.reminder_emails.length ? org.reminder_emails : org.contact_email ? [org.contact_email] : []);
  const [days, setDays] = useState((org.reminder_days ?? [60, 30, 7, 0]).join(", "));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  async function save(e: FormEvent) {
    e.preventDefault(); setBusy(true); setErr(null);
    try {
      const r = await orgApi("PATCH", `settings/${org.id}`, {
        reminder_emails: to, reminder_days: days.split(/[,;\s]+/).filter(Boolean).map(Number),
      });
      onSaved(r);
    } catch (x: any) { setErr(x.message); } finally { setBusy(false); }
  }
  return (
    <Drawer title="Renewal reminders" onClose={onClose}
      footer={<><button className="btn-ghost" onClick={onClose}>Cancel</button><button className="btn-primary" form="rem-form" disabled={busy}>Save</button></>}>
      <form id="rem-form" className="aform" onSubmit={save}>
        <Notice>Each morning around 7am (Fiji), we email a summary of registrations, inspections, insurance and permits that are about to expire or have expired.</Notice>
        <Field label="Send reminders to" wide hint="Pick company users or saved contacts, or type any email address. Contacts are managed on the Company page.">
          <RecipientPicker org={org.id} value={to} onChange={setTo} />
        </Field>
        <Field label="Remind this many days before expiry" wide hint="e.g. 60, 30, 7, 0 — one email per item at each step, plus once when it has expired.">
          <input value={days} onChange={(e) => setDays(e.target.value)} />
        </Field>
        {err && <div className="error wide">{err}</div>}
      </form>
    </Drawer>
  );
}
